import { themeStyle } from '@repo/ui/theming';
import styled, { css, keyframes } from 'styled-components';

import { GradientType } from '../../utils/color';
import { ColorStop } from '../ColorPicker/Gradient/GradientBar/types';

export interface ApplyFontStylesProps {
  fontFamily?: string;
  fontWeight?: number;
  fontSize?: string;
  lineHeight?: string;
}

/**
 * Apply default font style attributes
 */
export const applyFontStyles = css<ApplyFontStylesProps>`
  ${(props) => (props.fontFamily ? `font-family:${props.fontFamily};` : '')};
  ${(props) => (props.fontWeight ? `font-weight:${props.fontWeight};` : '')};
  ${(props) => (props.fontSize ? `font-size:${props.fontSize};` : '')};
  ${(props) => (props.lineHeight ? `line-Height:${props.lineHeight};` : '')};
`;

export const Background = styled.div<{
  background: string;
  isLoading: boolean;
}>`
  // no background after loaded, to avoid side effect of transparent color overlapping
  background: ${({ background, isLoading }) =>
    isLoading ? background : 'none'};
  height: 100%;
  width: 100%;
`;

export const Message = styled.div`
  font-weight: ${themeStyle.fontMedium};
  font-size: ${themeStyle.fontSize12};
  line-height: 175%;
  color: ${themeStyle.inkMedium};

  margin: auto;
  max-width: 400px;
  text-align: center;
`;

export const DividerWrapper = styled.div`
  cursor: default;
`;

export const Divider = styled.div<{ color?: string; colorDark?: string }>`
  width: 100%;
  height: 1px;
  border-left: none;
  border-right: none;
  border-bottom: none;
  border-top: 1px solid ${({ color }) => color ?? themeStyle.varInkLight};

  html[data-theme='dark'] &&,
  .dark && {
    border-top: 1px solid
      ${({ color, colorDark }) =>
        colorDark ?? color ?? themeStyle.varInkSuperLight};
  }
`;

// colorStops is expected to have two or more items
export const createGradient = (
  colorStops: ColorStop[],
  angle?: number,
  type?: GradientType
) => {
  colorStops = [...colorStops].sort((a, b) => a.offset - b.offset);
  const colorsString = colorStops.reduce((acc, stop, i) => {
    const color = stop.color === null ? themeStyle.transparent : stop.color;
    return (
      acc +
      `${color} ${stop.offset * 100}% ` +
      (i !== colorStops.length - 1 ? ',\n' : '')
    );
  }, '');

  if (type === GradientType.Radial) {
    return `radial-gradient(circle at center, ${colorsString})`;
  }

  return `
        linear-gradient(
            ${angle === undefined ? 'to right' : `${angle}deg`},
            ${colorsString}
        )
    `;
};

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
    margin-bottom: -10px;
  }
  to {
    opacity: 1;
    transform: translateY(0px);
    margin-bottom: 0;
  }
`;

export const slideInAnimationRule = css`
  ${slideIn};
`;

/**
 * A blank div is often used as a utility for children from Popovers
 * - it can forward the ref, when the children are not able to do so
 * - it can avoid unwanted properties from the Popover to leak into the children (like isActive)
 * - it can allow to nest Popovers and Tooltips
 */
export const BlankDiv = styled.div``;
